import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const WorkspaceContext = createContext(null);

export function useWorkspace() {
  return useContext(WorkspaceContext);
}

export function WorkspaceProvider({ children }) {
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) loadWorkspace(session.user.id);
      else setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkspace = async (userId) => {
    setLoading(true);

    const { data: memberRow } = await supabase
      .from('workspace_members')
      .select('workspace_id, role')
      .eq('user_id', userId)
      .maybeSingle();

    let wsId;

    if (memberRow) {
      wsId = memberRow.workspace_id;
      setUserRole(memberRow.role);
    } else {
      const { data: profile } = await supabase
        .from('profiles')
        .select('fund_name, full_name, email')
        .eq('id', userId)
        .maybeSingle();

      const { data: newWs } = await supabase
        .from('workspaces')
        .insert({ fund_name: profile?.fund_name || 'My Fund', owner_id: userId })
        .select()
        .single();

      wsId = newWs?.id;

      await supabase.from('workspace_members').insert({
        workspace_id: wsId,
        user_id: userId,
        email: profile?.email || '',
        full_name: profile?.full_name || '',
        role: 'Partner',
      });

      setUserRole('Partner');
    }

    const { data: ws } = await supabase
      .from('workspaces')
      .select('*')
      .eq('id', wsId)
      .single();
    setWorkspace(ws);

    const { data: allMembers } = await supabase
      .from('workspace_members')
      .select('*')
      .eq('workspace_id', wsId)
      .order('created_at', { ascending: true });
    setMembers(allMembers || []);

    setLoading(false);
  };

  const inviteMember = async (email, role) => {
    if (userRole !== 'Partner') throw new Error('Only Partners can invite members');
    const { data: { session } } = await supabase.auth.getSession();
    const { error } = await supabase.from('workspace_invites').insert({
      workspace_id: workspace?.id,
      email,
      role,
      invited_by: session?.user?.id,
    });
    if (error) throw error;
    await logActivity('invited_member', 'member', null, email, { role });
  };

  const logActivity = async (action, entityType, entityId, entityName, metadata = {}) => {
    if (!workspace?.id) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const currentMember = members.find(m => m.user_id === session.user.id);
      await supabase.from('activity_feed').insert({
        workspace_id: workspace.id,
        user_id: session.user.id,
        user_name: currentMember?.full_name || session.user.email,
        action,
        entity_type: entityType,
        entity_id: entityId ? String(entityId) : null,
        entity_name: entityName,
        metadata,
      });
    } catch { /* silent */ }
  };

  return (
    <WorkspaceContext.Provider value={{ workspace, members, userRole, loading, loadWorkspace, inviteMember, logActivity }}>
      {children}
    </WorkspaceContext.Provider>
  );
}
